import {
  cohortColorsByName,
  mergeConversionNodes,
  mergeConversionLinks,
  mergeCohortNodes,
  mergeCohortLinks,
  recomputeConversionMergedPercentages,
  recomputeCohortMergedPercentages,
  rebuildAnchorsFromMergedNodes,
  SEG_CONVERTED,
  SEG_DID_NOT,
} from "./flowSegmentMerge";

/**
 * Top paths must use the same population as the conversion headline (converted users only).
 * `prepareFlowDisplayData` merges conversion segments and sets each link `value` to
 * converted + did-not-convert, which inflates path volumes vs the last anchor's `convertedCount`.
 * Cohort compare merges all cohorts into one total — also invalid for per-path sums.
 */
export function prepareFlowTopPathsData(raw) {
  const data = raw || {};
  const bd = data.breakdown;

  if (bd?.mode === "conversion" && bd.data?.[SEG_CONVERTED]) {
    const slice = bd.data[SEG_CONVERTED];
    return {
      ...data,
      nodes: slice.nodes || [],
      links: slice.links || [],
    };
  }

  if (bd?.mode === "cohorts" && bd.data && ((data.nodes && data.nodes.length) || (data.links && data.links.length))) {
    return {
      ...data,
      nodes: data.nodes || [],
      links: data.links || [],
    };
  }

  return prepareFlowDisplayData(data);
}

/**
 * Resolve flow API payload to display-ready nodes/links/anchors (same rules as FlowVisualization).
 */
export function prepareFlowDisplayData(raw) {
  const data = raw || {};
  let anchors = data.anchors || [];
  const bd = data.breakdown;

  if (bd?.mode === "cohorts" && bd?.values?.length > 0 && bd?.data) {
    const order = bd.values;
    const segmentColors = cohortColorsByName(order);
    const mergedNodes = mergeCohortNodes(order, bd.data);
    anchors = rebuildAnchorsFromMergedNodes(mergedNodes, anchors);
    const nodes = recomputeCohortMergedPercentages(mergedNodes, anchors);
    const links = mergeCohortLinks(order, bd.data);
    return {
      ...data,
      nodes,
      links,
      anchors,
      segmentOrder: order,
      segmentColors,
    };
  }

  if (bd?.mode === "conversion" && bd?.data?.[SEG_CONVERTED] && bd?.data?.[SEG_DID_NOT]) {
    const conv = bd.data[SEG_CONVERTED];
    const dnc = bd.data[SEG_DID_NOT];
    const mergedNodes = mergeConversionNodes(conv.nodes || [], dnc.nodes || []);
    const nodes = recomputeConversionMergedPercentages(mergedNodes, anchors);
    const links = mergeConversionLinks(conv.links || [], dnc.links || []);
    return {
      ...data,
      nodes,
      links,
      anchors,
      segmentOrder: null,
      segmentColors: null,
    };
  }

  return {
    ...data,
    nodes: data.nodes || [],
    links: data.links || [],
    anchors,
    segmentOrder: null,
    segmentColors: null,
  };
}
